import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Measures the space between a container's top edge and the bottom of the viewport,
 * so its content (e.g. Kanban board columns) fills exactly to the bottom of the screen
 * with a fixed margin — instead of a guessed vh percentage that either overflows the
 * viewport (columns grow past the fold) or leaves a gap underneath.
 *
 * Recalculates on window resize and whenever a value in `recalcDeps` changes (pass
 * anything that can change the height of content above the measured container, e.g.
 * a conditionally-rendered banner or org name).
 */
export const useFillViewportHeight = (bottomMargin = 16, recalcDeps = []) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const recalc = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      const available = window.innerHeight - top - bottomMargin;
      setHeight(Math.max(available, 0));
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomMargin, ...recalcDeps]);

  return [ref, height];
};
